import React from 'react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { documentFilterState } from '@/recoil/atom';
import {
  useGetRecentTalkFlowAttendees,
  useGetTalkFlowFilterRecommend,
} from '@/hooks/apiHooks';
import {
  DocumentTypeFilter,
  DocumentTypeFilterLabel,
  SearchData,
} from '@/types/api';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import SearchDocument from '@/components/Approval/Content/SearchDocument';
import DocumentPeoplePanel from '@/components/Approval/Content/DocumentContents/DocumentFilterBar/DocumentPeoplePanel';
import DocumentPeriodPanel from '@/components/Approval/Content/DocumentContents/DocumentFilterBar/DocumentPeriodPanel';
import Favorites from '@/components/Approval/Content/DocumentContents/Favorites';
import FilterChipPopover from '@/components/Approval/RightContainer/FilterBar/FilterChipPopover';
import { ChipSummary } from '@/components/Approval/RightContainer/FilterBar/chipSummary';
import {
  ActionRow,
  ClearAllButton,
  FilterRow,
} from '@/components/Approval/RightContainer/FilterBar/styles';

type Props = {
  isTablet: boolean;
};

const DOCUMENT_TYPES: DocumentTypeFilter[] = ['note,new', 'meeting'];

/** 첫 항목 + 나머지 개수 요약 */
const toSummary = (labels: string[]): ChipSummary =>
  labels.length ? { label: labels[0], extraCount: labels.length - 1 } : null;

const rangeSummary = (start?: string, end?: string): ChipSummary =>
  start || end
    ? { label: `${start ?? ''} ~ ${end ?? ''}`, extraCount: 0 }
    : null;

/**
 * 회의록(Meeting Notes) 본문 상단 필터 바.
 * 전자결재 필터 바와 동일한 칩 팝오버 시스템을 쓰되, 데이터는 documentFilterState 를 사용한다.
 * 회의록은 필터 항목이 적어 '필터 더보기' 없이 전부 기본 칩으로 노출한다.
 * 1행: 검색 + 양식/작성자/내부 참석자/회의 일시/등록기간 칩
 * 2행: 전체해제 + (전체 문서 카테고리에서 필터가 있을 때) 내 필터로 저장
 */
const DocumentFilterBar = ({ isTablet }: Props) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useAtom(documentFilterState);
  const { reset } = useFormContext<SearchData>();
  // 내부 참석자 추천(최근 참석자)
  const { data: recentAttendees } = useGetRecentTalkFlowAttendees();

  // 작성자 추천: 필터 사용 이력 기반 '최근 작성자' — 응답이 평면 구조라 detail로 감싸 맞춘다
  const { data: filterRecommend } = useGetTalkFlowFilterRecommend();
  const authorRecommend = filterRecommend?.createdBys.map((p) => ({
    passportId: p.passportId,
    detail: {
      name: p.name,
      positionName: p.positionName,
      divisionName: p.divisionName,
      departmentName: p.departmentName,
      profileImageUrl: p.profileImageUrl,
    },
  }));

  const typeSummary = toSummary(
    filters.documentTypes.map((type) => t(DocumentTypeFilterLabel[type])),
  );
  const authorSummary = toSummary(filters.createdBy.map((p) => p.name));
  const attendeeSummary = toSummary(
    filters.internalParticipants.map((p) => p.name),
  );
  const meetingSummary = rangeSummary(
    filters.meetingStartedAt,
    filters.meetingEndedAt,
  );
  const createdSummary = rangeSummary(
    filters.createdStartedAt,
    filters.createdEndedAt,
  );

  const toggleDocumentType = (type: DocumentTypeFilter) => {
    setFilters({
      ...filters,
      documentTypes: filters.documentTypes.includes(type)
        ? filters.documentTypes.filter((item) => item !== type)
        : [...filters.documentTypes, type],
    });
  };

  const haveFilters =
    filters.documentTypes.length > 0 ||
    filters.createdBy.length > 0 ||
    filters.internalParticipants.length > 0 ||
    !!filters.meetingStartedAt ||
    !!filters.meetingEndedAt ||
    !!filters.createdStartedAt ||
    !!filters.createdEndedAt ||
    filters.keyword.length > 0;

  // 전자결재 FilterBar 와 동일 — '전체 문서' 카테고리에서 필터가 걸려 있을 때만 저장 가능
  const applyFavoriteFilters = filters.category === 'all' && haveFilters;

  const onClearAll = () => {
    setFilters({
      ...filters,
      documentTypes: [],
      createdBy: [],
      internalParticipants: [],
      requireAllInternalParticipants: false,
      createdStartedAt: undefined,
      createdEndedAt: undefined,
      meetingStartedAt: undefined,
      meetingEndedAt: undefined,
      keyword: '',
      searchFields: 'subject,content',
    });
    // 검색 입력창(react-hook-form)의 잔여 텍스트도 함께 초기화 (전자결재 FilterBar 와 동일)
    reset();
  };

  return (
    <div style={{ paddingTop: '16px' }}>
      <FilterRow>
        <div style={{ flexShrink: 0, width: 'auto' }}>
          <SearchDocument isTablet={isTablet} hideSort />
        </div>
        <FilterChipPopover
          label={t('양식')}
          summary={typeSummary}
          popoverWidth={280}
        >
          <List>
            {DOCUMENT_TYPES.map((type) => (
              <Row
                key={type}
                className={'v2cb-hover-target'}
                onClick={() => toggleDocumentType(type)}
              >
                <Checkbox
                  checked={filters.documentTypes.includes(type)}
                  sx={{ padding: 0 }}
                />
                <Label>{t(DocumentTypeFilterLabel[type])}</Label>
              </Row>
            ))}
          </List>
        </FilterChipPopover>
        <FilterChipPopover
          label={t('작성자')}
          summary={authorSummary}
          popoverWidth={360}
        >
          <DocumentPeoplePanel
            field={'createdBy'}
            placeholder={t('이름, ID, 닉네임 등을 검색해 보세요')}
            recommend={authorRecommend}
          />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('내부 참석자')}
          summary={attendeeSummary}
          popoverWidth={360}
        >
          <DocumentPeoplePanel
            field={'internalParticipants'}
            placeholder={t('이름, ID, 닉네임 등을 검색해 보세요')}
            recommend={recentAttendees ?? undefined}
            allFilter={{
              checked: filters.requireAllInternalParticipants,
              onChange: (all) =>
                setFilters({ ...filters, requireAllInternalParticipants: all }),
            }}
          />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('회의 일시')}
          summary={meetingSummary}
          popoverWidth={420}
          onClear={() =>
            setFilters({
              ...filters,
              meetingStartedAt: undefined,
              meetingEndedAt: undefined,
            })
          }
        >
          <DocumentPeriodPanel filterType={'meeting'} />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('등록기간')}
          summary={createdSummary}
          popoverWidth={420}
          onClear={() =>
            setFilters({
              ...filters,
              createdStartedAt: undefined,
              createdEndedAt: undefined,
            })
          }
        >
          <DocumentPeriodPanel filterType={'created'} />
        </FilterChipPopover>
      </FilterRow>

      {haveFilters && (
        <ActionRow>
          <ClearAllButton type={'button'} onClick={onClearAll}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10C4 8.11516 4.86911 6.4333 6.22844 5.33333M4 4.99984H6.66667V7.66651"
                stroke="#0066CC"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('전체해제')}
          </ClearAllButton>
          {applyFavoriteFilters && <Favorites />}
        </ActionRow>
      )}
    </div>
  );
};

export default DocumentFilterBar;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  * {
    cursor: pointer;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: var(--dk-text, black);
`;
